(() => {
  if (window.pdpPeptideSystemReady) return;
  window.pdpPeptideSystemReady = true;

  const initGallery = (root) => {
    if (root.dataset.pdpGalleryReady === "true") return;
    const stage = root.querySelector(".pdp-peptide-gallery__stage");
    const panels = root.querySelectorAll("[data-pdp-gallery-panel]");
    const buttons = root.querySelectorAll("[data-pdp-gallery-thumb]");
    if (!stage || !panels.length || !buttons.length) return;
    root.dataset.pdpGalleryReady = "true";
    const buttonList = Array.from(buttons);
    const thumbnailList = root.querySelector(".pdp-peptide-gallery__thumbs");
    const previousButton = root.querySelector("[data-pdp-gallery-previous]");
    const nextButton = root.querySelector("[data-pdp-gallery-next]");

    const syncStageRatio = (panel) => {
      const ratio = Number.parseFloat(
        panel?.getAttribute("data-pdp-gallery-aspect-ratio")
      );
      if (!Number.isFinite(ratio) || ratio <= 0) return;
      stage.style.setProperty("--pdp-gallery-aspect-ratio", String(ratio));
    };

    syncStageRatio(Array.from(panels).find((panel) => !panel.hidden));

    const activate = (button) => {
      const target = button.getAttribute("data-pdp-gallery-thumb");
      let activePanel;
      panels.forEach((panel) => {
        const active = panel.getAttribute("data-pdp-gallery-panel") === target;
        panel.hidden = !active;
        if (active) activePanel = panel;
        const video = panel.querySelector("video");
        if (video && !active) video.pause();
      });
      syncStageRatio(activePanel);
      buttons.forEach((item) => {
        item.classList.toggle("is-active", item === button);
        item.setAttribute("aria-selected", item === button ? "true" : "false");
        item.setAttribute("tabindex", item === button ? "0" : "-1");
      });
      if (thumbnailList) {
        const buttonStart = button.offsetLeft;
        const buttonEnd = buttonStart + button.offsetWidth;
        const visibleStart = thumbnailList.scrollLeft;
        const visibleEnd = visibleStart + thumbnailList.clientWidth;
        let scrollLeft = visibleStart;
        if (buttonStart < visibleStart) scrollLeft = buttonStart;
        if (buttonEnd > visibleEnd) {
          scrollLeft = buttonEnd - thumbnailList.clientWidth;
        }
        if (scrollLeft !== visibleStart) {
          thumbnailList.scrollTo({
            left: scrollLeft,
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
              .matches
              ? "auto"
              : "smooth",
          });
        }
      }
    };

    const move = (offset) => {
      const currentIndex = buttonList.findIndex(
        (button) => button.getAttribute("aria-selected") === "true"
      );
      const nextIndex =
        (Math.max(currentIndex, 0) + offset + buttonList.length) %
        buttonList.length;
      const nextButton = buttonList[nextIndex];
      activate(nextButton);
    };

    buttons.forEach((button) => {
      button.addEventListener("click", () => activate(button));
      button.addEventListener("keydown", (event) => {
        const currentIndex = buttonList.indexOf(button);
        let nextIndex = currentIndex;

        if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % buttons.length;
        if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = buttons.length - 1;
        if (nextIndex === currentIndex) return;

        event.preventDefault();
        activate(buttonList[nextIndex]);
        buttonList[nextIndex].focus();
      });
    });

    stage.addEventListener("keydown", (event) => {
      if (event.target !== stage) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      move(event.key === "ArrowRight" ? 1 : -1);
    });

    previousButton?.addEventListener("click", () => move(-1));
    nextButton?.addEventListener("click", () => move(1));

    let gesture;
    const resetGesture = () => {
      gesture = undefined;
    };

    stage.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" || event.button !== 0) return;
      if (
        event.target.closest(
          "button, a, input, select, textarea, video, iframe, model-viewer"
        )
      ) {
        return;
      }
      gesture = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        startedAt: performance.now(),
      };
      stage.setPointerCapture?.(event.pointerId);
    });

    stage.addEventListener("pointerup", (event) => {
      if (!gesture || gesture.id !== event.pointerId) return;
      const deltaX = event.clientX - gesture.x;
      const deltaY = event.clientY - gesture.y;
      const elapsed = performance.now() - gesture.startedAt;
      const swipeDistance = Math.min(
        80,
        Math.max(40, stage.clientWidth * 0.12)
      );
      resetGesture();

      if (
        elapsed <= 800 &&
        Math.abs(deltaX) >= swipeDistance &&
        Math.abs(deltaX) > Math.abs(deltaY) * 1.2
      ) {
        move(deltaX < 0 ? 1 : -1);
      }
    });

    stage.addEventListener("pointercancel", resetGesture);
  };

  const initProductForm = (root) => {
    if (root.dataset.pdpProductFormReady === "true") return;
    const formId = root.getAttribute("data-pdp-product-form");
    const form = formId ? document.getElementById(formId) : null;
    if (!form) return;
    root.dataset.pdpProductFormReady = "true";
    const allowSellingPlans =
      root.getAttribute("data-pdp-allow-selling-plans") === "true";

    const submitButtons = [
      ...form.querySelectorAll('[name="add"]'),
      ...document.querySelectorAll(`[form="${formId}"][name="add"]`),
    ];

    const showError = (message) => {
      const errorMessage = message || root.getAttribute("data-pdp-cart-error");
      const messageElement = form
        .closest("product-form")
        ?.querySelector("custom-message");

      if (messageElement) {
        const messageText = messageElement.querySelector("[message-text]");
        if (messageText) messageText.textContent = errorMessage;
        messageElement.classList.add("error", "d-block");
        messageElement.classList.remove("d-none");
      }

      document.dispatchEvent(
        new CustomEvent("showToastMessage", {
          detail: {
            toastType: "common-errors",
            messageType: "error",
            message: errorMessage,
          },
        })
      );
    };

    form.addEventListener(
      "submit",
      async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (form.dataset.pdpSubmitting === "true") return;

        form.dataset.pdpSubmitting = "true";
        form.setAttribute("aria-busy", "true");
        const initialButtonStates = submitButtons.map((button) => button.disabled);
        submitButtons.forEach((button) => {
          button.disabled = true;
          button.classList.add("loadingState");
        });

        try {
          const formData = new FormData(form);
          if (allowSellingPlans) {
            const selectedSellingPlan = root.querySelector(
              'input[name="selling_plan"]:checked'
            );
            if (selectedSellingPlan) {
              if (selectedSellingPlan.value) {
                formData.set("selling_plan", selectedSellingPlan.value);
              } else {
                formData.delete("selling_plan");
              }
            }
          } else {
            formData.delete("selling_plan");
          }

          const response = await fetch(
            `${window.Shopify.routes.root}cart/add.js`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
              },
              body: formData,
            }
          );
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(
              data.description ||
                data.message ||
                root.getAttribute("data-pdp-cart-error")
            );
          }

          if (document.querySelector("drawer-cart")) {
            document.dispatchEvent(
              new CustomEvent("action:cart:updateCart", {
                detail: {
                  openDrawer: true,
                  productVariant: data.variant_id,
                },
              })
            );
          } else {
            window.location.href = `${window.Shopify.routes.root}cart`;
          }
        } catch (error) {
          showError(error.message);
        } finally {
          delete form.dataset.pdpSubmitting;
          form.removeAttribute("aria-busy");
          submitButtons.forEach((button, index) => {
            button.disabled = initialButtonStates[index];
            button.classList.remove("loadingState");
          });
        }
      },
      true
    );
  };

  const initSticky = (sticky) => {
    if (sticky.dataset.pdpStickyReady === "true") return;
    const trigger = document.querySelector("[data-pdp-sticky-trigger]");
    if (!trigger) return;
    sticky.dataset.pdpStickyReady = "true";

    const formId = sticky.getAttribute("data-pdp-sticky-form");
    const form = formId ? document.getElementById(formId) : null;
    const formSubmit = form?.querySelector('[name="add"]');
    const stickySubmit = sticky.querySelector("[data-pdp-sticky-submit]");

    const syncSubmitState = () => {
      if (!formSubmit || !stickySubmit) return;
      stickySubmit.disabled = formSubmit.disabled;
    };

    const setActive = () => {
      const rect = (form || trigger).getBoundingClientRect();
      sticky.classList.toggle("is-active", rect.bottom < 0);
    };

    setActive();
    syncSubmitState();
    document.addEventListener("scroll", setActive, { passive: true });
    window.addEventListener("resize", setActive);

    if (formSubmit) {
      new MutationObserver(syncSubmitState).observe(formSubmit, {
        attributes: true,
        attributeFilter: ["disabled"],
      });
    }
  };

  const initReviewFilters = (root) => {
    if (root.dataset.pdpReviewsReady === "true") return;
    const filters = root.querySelectorAll("[data-pdp-review-filter]");
    const reviews = root.querySelectorAll("[data-pdp-review-card]");
    if (!filters.length || !reviews.length) return;
    root.dataset.pdpReviewsReady = "true";

    filters.forEach((filter) => {
      filter.addEventListener("click", () => {
        const value = filter.getAttribute("data-pdp-review-filter");
        filters.forEach((item) => {
          const active = item === filter;
          item.classList.toggle("is-active", active);
          item.setAttribute("aria-pressed", active ? "true" : "false");
        });
        reviews.forEach((review) => {
          const tags = review.getAttribute("data-pdp-review-card") || "";
          review.hidden = value !== "all" && !tags.split(",").map((tag) => tag.trim()).includes(value);
        });
      });
    });
  };

  const initPriceSync = (root) => {
    if (root.dataset.pdpPriceSyncReady === "true") return;
    const priceRegion = root.querySelector("[data-pdp-price-region]");
    if (!priceRegion) return;
    root.dataset.pdpPriceSyncReady = "true";

    const syncPrice = () => {
      const source = priceRegion.querySelector("[data-pdp-price-source]");
      const price = source?.textContent.trim();
      if (!price) return;
      document.querySelectorAll("[data-pdp-product-price]").forEach((target) => {
        target.textContent = price;
      });
    };

    syncPrice();
    new MutationObserver(syncPrice).observe(priceRegion, {
      childList: true,
      subtree: true,
    });
  };

  const initPurchaseLink = (link) => {
    if (link.dataset.pdpPurchaseLinkReady === "true") return;
    link.dataset.pdpPurchaseLinkReady = "true";

    link.addEventListener("click", (event) => {
      const target = document.querySelector(link.getAttribute("href"));
      const submit = target?.querySelector('[name="add"]');
      if (!target || !submit) return;
      event.preventDefault();

      const targetRect = target.getBoundingClientRect();
      const submitRect = submit.getBoundingClientRect();
      const sticky = document.querySelector("[data-pdp-sticky].is-active");
      const headerBottom = Array.from(document.querySelectorAll("header"))
        .map((header) => {
          const position = window.getComputedStyle(header).position;
          const rect = header.getBoundingClientRect();
          if (!['fixed', 'sticky'].includes(position) || rect.bottom <= 0 || rect.top > 0) return 0;
          return rect.bottom;
        })
        .reduce((largest, bottom) => Math.max(largest, bottom), 0);
      const topOffset = Math.max(20, headerBottom + 12);
      const bottomOffset = 20 + (sticky?.offsetHeight || 0);
      const targetTop = window.scrollY + targetRect.top;
      const submitBottom = window.scrollY + submitRect.bottom;
      const scrollTop = Math.max(
        targetTop - topOffset,
        submitBottom - (window.innerHeight - bottomOffset)
      );
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      window.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: reduceMotion ? "auto" : "smooth",
      });
      target.classList.add("is-attention");
      window.setTimeout(() => {
        target.classList.remove("is-attention");
        submit.focus({ preventScroll: true });
      }, reduceMotion ? 0 : 550);
    });
  };

  const init = () => {
    document.querySelectorAll("[data-pdp-gallery]").forEach(initGallery);
    document.querySelectorAll("[data-pdp-sticky]").forEach(initSticky);
    document.querySelectorAll("[data-pdp-reviews]").forEach(initReviewFilters);
    document.querySelectorAll("[data-pdp-sticky-trigger]").forEach(initPriceSync);
    document.querySelectorAll("[data-pdp-sticky-trigger]").forEach(initProductForm);
    document.querySelectorAll("[data-pdp-scroll-to-purchase]").forEach(initPurchaseLink);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
